import {dogBreeds} from '../data/breeds.js';

export class BreedMatcher {
  constructor(userResponses) {
    this.responses=userResponses;
    this.scores=new Map();
  }

  calculateMatches() {
    // Reset scores
    this.scores.clear();

    // Initialize all breeds with base score to avoid 0% for decent matches
    dogBreeds.forEach(breed=> {
      this.scores.set(breed.id,{
        breed,
        score: 40, // Start with a healthy base score
        factors: {},
        penalties: [],
        bonuses: []
      });
    });

    // Apply all matching criteria
    this.matchLivingSituation();
    this.matchActivityLevel();
    this.matchPersonality();
    this.matchCareCapabilities();
    this.matchClimate();
    this.matchPhysicalCompatibility();
    this.matchLifestyleFactors();
    this.matchEmotionalNeeds();

    // Normalize scores to 0-100%
    // A perfect match usually accumulates around 100-110 points on top of base
    const MAX_SCORE_BASELINE = 110;

    this.scores.forEach(breedScore => {
        // Calculate percentage 
        let percentage = (breedScore.score / MAX_SCORE_BASELINE) * 100;
        
        // Curve grades up slightly ensures "Good" matches feel good (80%+)
        percentage = percentage * 1.1;

        // Clamp between 0 and 100
        percentage = Math.max(0, Math.min(100, percentage));
        
        breedScore.score = percentage;
    });

    // Convert to array and sort by score
    const results=Array.from(this.scores.values())
      .sort((a,b)=> b.score - a.score)
      .slice(0,20);// Top 20 matches

    return results;
  }

  matchLivingSituation() {
    const housingType=this.responses['housing-type'];
    const yardSpace=this.responses['yard-space'];
    const householdMembers=this.responses['household-members'] || [];
    const otherPets=this.responses['other-pets'] || [];

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      // Housing compatibility
      if (breed.livingSpace.includes(this.mapHousingType(housingType))) {
        score +=20;
        breedScore.bonuses.push('Fits your home size');
      } else {
        score -=25; // Stronger penalty for wrong housing
        breedScore.penalties.push('May need more space');
      }

      // Yard space requirements
      if (breed.exerciseNeeds==='Very High' && yardSpace==='none') {
        score -=20;
        breedScore.penalties.push('Needs a yard for exercise');
      } else if (breed.exerciseNeeds==='High' && ['none','balcony'].includes(yardSpace)) {
        score -=15;
        breedScore.penalties.push('Limited outdoor space');
      }

      // Children compatibility
      const hasYoungChildren=householdMembers.includes('children-under-5') || householdMembers.includes('children-5-12');
      if (hasYoungChildren) {
        if (breed.goodWithKids==='Excellent') {
          score +=15;
          breedScore.bonuses.push('Great with kids');
        } else if (breed.goodWithKids==='Fair') {
          score -=15;
          breedScore.penalties.push('Not recommended for young kids');
        }
      }

      // Other pets compatibility
      const hasOtherPets=!otherPets.includes('none') && otherPets.length > 0;
      if (hasOtherPets) {
        if (breed.goodWithPets==='Excellent') {
          score +=10;
          breedScore.bonuses.push('Friendly with other pets');
        } else if (breed.goodWithPets==='Fair') {
          score -=10;
          breedScore.penalties.push('May not like other pets');
        }
      }

      breedScore.score +=score;
      breedScore.factors.living=score;
    });
  }

  matchActivityLevel() {
    const userActivity=this.responses['activity-level'];
    const exerciseCommitment=this.responses['exercise-commitment'];
    const outdoorActivities=this.responses['outdoor-activities'] || [];

    // Numeric mapping for calculation
    const activityMapping={'sedentary': 1,'light': 2,'moderate': 3,'active': 4,'very-active': 5};
    const exerciseMapping={'minimal': 1,'low': 2,'moderate': 3,'high': 4,'very-high': 5};
    
    // UPDATED: More granular breed mapping to distinguish High from Medium-High
    const breedEnergyMapping={
        'Low': 1,
        'Low-Medium': 2,
        'Medium': 3,
        'Medium-High': 4,
        'High': 4.5, // Differentiate from Medium-High
        'Very High': 5
    };

    const userActivityScore=activityMapping[userActivity] || 3;
    const userExerciseScore=exerciseMapping[exerciseCommitment] || 3;
    const avgUserScore=(userActivityScore + userExerciseScore) / 2;

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      const breedEnergyScore=breedEnergyMapping[breed.energyLevel] || 3;

      // Calculate compatibility based on energy level match
      const energyDiff=Math.abs(avgUserScore - breedEnergyScore);
      let score=0;

      // UPDATED: Logic to cover scoring gaps and smooth the curve
      if (energyDiff <= 0.5) {
        score += 25; // Perfect or near-perfect match
        breedScore.bonuses.push('Perfect energy match');
      } else if (energyDiff <= 1.5) {
        score += 15; // Good match
        breedScore.bonuses.push('Good energy match');
      } else if (energyDiff <= 2.0) {
        score -= 5; // Slight mismatch - manageable but not ideal
        breedScore.penalties.push('Energy level slightly mismatched');
      } else {
        score -= 25; // Significant mismatch
        breedScore.penalties.push('Significant energy level mismatch');
      }

      // Bonus for outdoor activity alignment
      const isOutdoorPerson=!outdoorActivities.includes('none') && outdoorActivities.length > 0;
      if (isOutdoorPerson && breedEnergyScore >= 4) {
        score +=10;
        breedScore.bonuses.push('Great adventure buddy');
      } else if (!isOutdoorPerson && breedEnergyScore <= 2) {
        score +=10;
        breedScore.bonuses.push('Happy indoors');
      }

      // Safety Safety: Penalty for sedentary person with high energy dog
      if (avgUserScore < 2 && breedEnergyScore >= 4) {
          score -= 20; 
          breedScore.penalties.push('Requires significantly more exercise than your lifestyle suggests');
      }

      breedScore.score +=score;
      breedScore.factors.activity=score;
    });
  }

  matchPersonality() {
    const personalityType=this.responses['personality-type'];
    const stressLevel=this.responses['stress-level'];
    const patienceLevel=this.responses['patience-level'];
    const noiseToleranceLevel=this.responses['noise-tolerance'];

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      // Personality matching
      if (personalityType==='introverted-calm' || personalityType==='introverted-anxious') {
        if (['Low','Low-Medium','Medium'].includes(breed.energyLevel)) {
          score +=10;
          breedScore.bonuses.push('Calm temperament');
        }
      } else if (personalityType==='extroverted-energetic') {
        if (['Medium-High','High','Very High'].includes(breed.energyLevel)) {
          score +=10;
          breedScore.bonuses.push('Matches your high energy');
        }
      }

      // Stress level matching
      if (['poorly','very-poorly'].includes(stressLevel)) {
        if (breed.trainingDifficulty==='Easy' || breed.personality.includes('Gentle')) {
          score +=10;
          breedScore.bonuses.push('Low-stress companion');
        }
      }

      // Patience level matching
      if (['impatient','very-impatient'].includes(patienceLevel)) {
        if (breed.trainingDifficulty==='Easy') {
          score +=15;
          breedScore.bonuses.push('Easy to train');
        } else if (breed.trainingDifficulty==='Hard') {
          score -=15;
          breedScore.penalties.push('Requires patience to train');
        }
      }

      // Noise tolerance
      if (['very-sensitive','sensitive'].includes(noiseToleranceLevel)) {
        if (breed.barking==='Low') {
          score +=15;
          breedScore.bonuses.push('Quiet breed');
        } else if (breed.barking==='High') {
          score -=15;
          breedScore.penalties.push('Can be vocal');
        }
      }

      breedScore.score +=score;
      breedScore.factors.personality=score;
    });
  }

  matchCareCapabilities() {
    const groomingCommitment=this.responses['grooming-commitment'];
    const trainingExperience=this.responses['training-experience'];
    const budget=this.responses['budget'];

    const groomingMapping={'minimal': 1,'low': 2,'moderate': 3,'high': 4,'professional': 5};
    const breedGroomingMapping={'Low': 1,'Medium': 3,'High': 5};

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      // Grooming compatibility
      const userGroomingLevel=groomingMapping[groomingCommitment] || 2;
      const breedGroomingLevel=breedGroomingMapping[breed.groomingNeeds] || 2;

      if (userGroomingLevel >=breedGroomingLevel) {
        score +=10;
        // Only show bonus if it's a specific match, not just meeting baseline
        if (breed.groomingNeeds === 'Low' && groomingCommitment === 'minimal') {
            breedScore.bonuses.push('Low maintenance grooming');
        }
      } else {
        const deficit=breedGroomingLevel - userGroomingLevel;
        score -=deficit * 5;
        breedScore.penalties.push('Higher grooming needs');
      }

      // Training experience matching
      if (trainingExperience==='none' || trainingExperience==='basic') {
        if (breed.trainingDifficulty==='Easy') {
          score +=15;
          breedScore.bonuses.push('Beginner friendly');
        } else if (breed.trainingDifficulty==='Hard') {
          score -=20;
          breedScore.penalties.push('Best for experienced owners');
        }
      }

      // Budget considerations
      if (budget==='low') {
        if (breed.groomingNeeds==='High') {
          score -=10;
          breedScore.penalties.push('High grooming costs');
        }
        if (breed.size === 'Extra Large' || breed.size === 'Large') {
           score -=5; // Larger dogs eat more/cost more
        }
      }

      breedScore.score +=score;
      breedScore.factors.care=score;
    });
  }

  matchClimate() {
    const userClimate=this.responses['climate'];

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      const climateMap={'cold': 'Cold','moderate': 'Moderate','warm': 'Warm','tropical': 'Warm','arid': 'Warm'};
      const mappedClimate=climateMap[userClimate];

      if (breed.climate.includes(mappedClimate)) {
        score +=5;
      } else {
        score -=10;
        breedScore.penalties.push('Not ideal for your climate');
      }

      breedScore.score +=score;
      breedScore.factors.climate=score;
    });
  }

  matchPhysicalCompatibility() {
    const age=this.responses['age'];

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      // Age-based compatibility
      if (['65+'].includes(age)) {
        if (['Low','Low-Medium'].includes(breed.energyLevel)) {
          score +=15;
          breedScore.bonuses.push('Manageable energy level');
        }
        if (['Extra Small','Small','Medium'].includes(breed.size)) {
          score +=10;
        }
        if (breed.size === 'Extra Large' || breed.energyLevel === 'Very High') {
             score -=15;
             breedScore.penalties.push('May be physically demanding');
        }
      }

      breedScore.score +=score;
      breedScore.factors.physical=score;
    });
  }

  matchLifestyleFactors() {
    const workSchedule=this.responses['work-schedule'];
    const travelFrequency=this.responses['travel-frequency'];
    const cleanlinessPreference=this.responses['cleanliness-preference'];

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      // Work schedule compatibility
      if (['full-time-long','shift-work'].includes(workSchedule)) {
        if (['Low','Low-Medium'].includes(breed.energyLevel)) {
          score +=10;
          breedScore.bonuses.push('Independent nature');
        } else if (['High','Very High'].includes(breed.energyLevel)) {
          score -=15;
          breedScore.penalties.push('Needs more daily time');
        }
      }

      // Travel frequency
      if (['weekly','frequent'].includes(travelFrequency)) {
        if (['Extra Small','Small'].includes(breed.size)) {
          score +=10;
          breedScore.bonuses.push('Travel-sized companion');
        } else {
            score -=5;
        }
      }

      // Cleanliness preferences
      if (['very-important','important'].includes(cleanlinessPreference)) {
        if (breed.shedding==='Low' || breed.shedding==='Very Low') {
          score +=15;
          breedScore.bonuses.push('Low shedding');
        } else if (breed.shedding==='High') {
          score -=15;
          breedScore.penalties.push('Heavy shedder');
        }
      }

      breedScore.score +=score;
      breedScore.factors.lifestyle=score;
    });
  }

  matchEmotionalNeeds() {
    const emotionalNeeds=this.responses['emotional-needs'] || [];

    dogBreeds.forEach(breed=> {
      const breedScore=this.scores.get(breed.id);
      let score=0;

      // Emotional needs matching
      if (emotionalNeeds.includes('emotional-support')) {
        if (breed.personality.includes('Gentle') || breed.personality.includes('Affectionate')) {
          score +=10;
          breedScore.bonuses.push('Emotionally supportive');
        }
      }
      if (emotionalNeeds.includes('security')) {
        if (breed.personality.includes('Alert') || breed.personality.includes('Protective')) {
          score +=10;
          breedScore.bonuses.push('Good watch dog');
        }
      }
      if (emotionalNeeds.includes('active-lifestyle')) {
        if (['High','Very High'].includes(breed.energyLevel)) {
          score +=10;
          breedScore.bonuses.push('Great fitness partner');
        }
      }

      breedScore.score +=score;
      breedScore.factors.emotional=score;
    });
  }

  mapHousingType(housingType) {
    const mapping={
      'apartment': 'Apartment',
      'small-house': 'Small House',
      'large-house': 'Large House',
      'farm': 'Farm'
    };
    return mapping[housingType] || 'Apartment';
  }
}